import React, { useEffect, useState } from 'react';
import { Route, useLocation, useParams } from 'react-router';
import { Redirect, Switch } from 'react-router-dom';
import { userHasAnalyticsEnabledRedir } from '../core/auth';
import { getFeatBitConfig } from '../core/config';
import { useFeatureFlag, useFbClient } from '../core/hooks/useFeatureFlag';
import {
  ANALYTICS_RESTRUCTURE_FLAG,
  ANALYTICS_RESTRUCTURE_FLAG_DEFAULT,
  REPORT_KEYS,
} from '../views/analytics/models';
import AnalyticsReport from '../views/analytics/containers/AnalyticsReport';
import FinancialReport from '../views/analytics/containers/FinancialReport';
import PowerBiReport from '../views/analytics/containers/PowerBiReport';

const ROUTE_OVERRIDES = {
  financial: FinancialReport,
};

const { sdkKey } = getFeatBitConfig();
const isFeatBitConfigured = Boolean(sdkKey);

const reportRoutes = Object.entries(REPORT_KEYS).map(([key, reportKey]) => ({
  slug: key.toLowerCase(),
  reportKey,
  title: `${key} Dashboard`,
}));

const DEFAULT_SLUG = 'overview';

const AnalyticsRoute = () => {
  const { site } = useParams();
  const { pathname } = useLocation();
  const fbClient = useFbClient();
  const analyticsRestructureEnabled = useFeatureFlag(
    ANALYTICS_RESTRUCTURE_FLAG,
    ANALYTICS_RESTRUCTURE_FLAG_DEFAULT
  );

  const activeSlug = reportRoutes.find((r) => pathname.endsWith(`/${r.slug}`))?.slug;

  const [visited, setVisited] = useState(() => new Set(activeSlug ? [activeSlug] : []));

  useEffect(() => {
    if (!activeSlug) return;
    setVisited((prev) => {
      if (prev.has(activeSlug)) return prev;
      const next = new Set(prev);
      next.add(activeSlug);
      return next;
    });
  }, [activeSlug]);

  if (isFeatBitConfigured && !fbClient) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  if (!analyticsRestructureEnabled) {
    return (
      <Switch>
        <Route path="/:site/analytics">
          <PowerBiReport />
        </Route>
      </Switch>
    );
  }

  if (!activeSlug) {
    return <Redirect to={`/${site}/analytics/${DEFAULT_SLUG}`} />;
  }

  return (
    <>
      {reportRoutes.map(({ slug, reportKey, title }) => {
        if (!visited.has(slug)) return null;
        const Component = ROUTE_OVERRIDES[slug] ?? AnalyticsReport;
        return (
          <div key={slug} style={{ display: slug === activeSlug ? 'block' : 'none' }}>
            <Component reportKey={reportKey} reportName={title} />
          </div>
        );
      })}
    </>
  );
};

export default userHasAnalyticsEnabledRedir(AnalyticsRoute);
